import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class PhoneFormatterService {

  constructor() { }

  /**
   * Validates Indian phone numbers
   * Accepts formats: +91 XXXXX XXXXX, 91XXXXXXXXXX, XXXXXXXXXX (starting with 6,7,8,9)
   */
  static indianPhoneValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }
    
    const cleanNumber = control.value.replace(/\D/g, '');
    
    // Indian mobile numbers: 10 digits starting with 6,7,8,9 or with country code +91
    if (!/^(\+91|91)?[6-9]\d{9}$/.test(cleanNumber)) {
      return { 
        indianPhone: { 
          message: 'Please enter a valid Indian mobile number (10 digits starting with 6,7,8,9)' 
        } 
      };
    }
    
    return null;
  }

  /**
   * Formats phone number as user types (Indian format)
   * Returns formatted string: +91 XXXXX XXXXX
   */
  formatIndianPhoneNumber(input: string): string {
    let value = input.replace(/\D/g, ''); // Remove non-digits
    
    // Handle country code +91
    if (value.startsWith('91') && value.length > 2) {
      const number = value.substring(2);
      if (number.length > 5) {
        return '+91 ' + number.substring(0, 5) + ' ' + number.substring(5, 10);
      } else if (number.length > 0) {
        return '+91 ' + number;
      } else {
        return '+91';
      }
    } else if (value.startsWith('91') && value.length <= 2) {
      return '+91';
    } else if (value.length > 0) {
      // Format Indian mobile number: +91 XXXXX XXXXX
      if (value.length > 5) {
        return '+91 ' + value.substring(0, 5) + ' ' + value.substring(5, 10);
      } else {
        return '+91 ' + value;
      }
    }
    
    return '';
  }

  /**
   * Validates if the input length is within Indian phone number limits
   */
  isValidLength(input: string): boolean {
    const digitsOnly = input.replace(/\D/g, '');
    return digitsOnly.length <= 12; // +91 + 10 digits maximum
  }

  /**
   * Extracts clean phone number (digits only) from formatted input
   */
  getCleanNumber(input: string): string {
    return input.replace(/\D/g, '');
  }

  /**
   * Gets the display format for phone numbers
   * Converts stored format to display format
   */
  getDisplayFormat(phoneNumber: string): string {
    if (!phoneNumber) return '';
    
    const cleanNumber = this.getCleanNumber(phoneNumber);
    
    if (cleanNumber.startsWith('91') && cleanNumber.length === 12) {
      const number = cleanNumber.substring(2);
      return `+91 ${number.substring(0, 5)} ${number.substring(5)}`;
    } else if (cleanNumber.length === 10 && /^[6-9]/.test(cleanNumber)) {
      return `+91 ${cleanNumber.substring(0, 5)} ${cleanNumber.substring(5)}`;
    }
    
    return phoneNumber; // Return as-is if doesn't match expected format
  }

  /**
   * Gets the storage format for phone numbers
   * Converts display format to storage format (with country code)
   */
  getStorageFormat(phoneNumber: string): string {
    if (!phoneNumber) return '';
    
    const cleanNumber = this.getCleanNumber(phoneNumber);
    
    if (cleanNumber.startsWith('91') && cleanNumber.length === 12) {
      return `+${cleanNumber}`;
    } else if (cleanNumber.length === 10 && /^[6-9]/.test(cleanNumber)) {
      return `+91${cleanNumber}`;
    }
    
    return phoneNumber; // Return as-is if doesn't match expected format
  }
}
